import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet, uiMoney, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { thUI } from "./threads.js";

/**
 * M&A — Acquisitions as threads (replaces app.js renderMA).
 *
 * Left: "Your deals" (one thread per acquisition, with its current step) and
 * "For sale this month" (your Chief of Staff's pitches). Open a deal and you
 * read its whole story: your offer, the books check, how you paid, the
 * Board, the price, the merger — each your reply or their letter, in order,
 * with the current letter's decisions at the bottom. Decide, and the reply
 * and the next letter appear in the same thread. Buying outright ends in a
 * completion notice. Shared view: sections/threads.js.
 * Below the desk, "Companies you own" (/api/ma/owned, backend/ma_owned.py): invest
 * to grow one, put your brand on it, or sell it to this quarter's buyers; each
 * links to Global Holdings → Companies you own (views/holdings.js) for its industry moves.
 * Data: /api/ma/threads (backend/correspondence_threads.py).
 */

const MA_PASSED_KEY = "ges.ma.dismissed";

function maPassed() {
  try { return JSON.parse(localStorage.getItem(MA_PASSED_KEY) || "{}"); } catch (e) { return {}; }
}

const maM = (v) => uiMoney(v, { compact: true });

/** One company you own: what it brings in, what it's worth, and the owner's moves. */
function maOwnedCard(o) {
  const best = o.buyers[0];
  const revUp = o.revenue >= o.revenue_at_purchase * 0.85;
  return `<article class="mx-own mo-card">
    <div class="mx-card-top"><h4>${esc(o.name)}</h4>${o.integrating ? uiPill("Merging: revenue still ramping up", "info")
      : uiPill(o.gain_if_sold >= 0 ? "Worth more than you paid" : "Worth less than you paid", o.gain_if_sold >= 0 ? "good" : "bad")}</div>
    <p class="mo-sub">Owned ${o.months_owned} month${o.months_owned === 1 ? "" : "s"} · ${o.employees} people · runs as “${esc(o.line)}”${o.rebranded ? " · carries your brand" : ""}</p>
    <div class="mx-facts">
      <div><span>You paid</span><b>${maM(o.paid)}</b></div>
      <div><span>Brings in now</span><b class="${revUp ? "" : "neg"}">${maM(o.revenue)}/yr</b></div>
      <div><span>Worth now</span><b>${maM(o.value)}</b></div>
      <div><span>If you sold today</span><b class="${o.gain_if_sold >= 0 ? "pos" : "neg"}">${o.gain_if_sold >= 0 ? "+" : ""}${maM(o.gain_if_sold)}</b></div>
    </div>
    ${o.boost_months ? `<p class="mo-sub">📈 Growth investment running: +${o.boost_pts} pts a month for ${o.boost_months} more months.</p>` : ""}
    ${o.quick_flip ? `<p class="mo-sub">Bought under 6 months ago: buyers knock 10% off a quick resale.</p>` : ""}
    <div class="mo-actions">
      <button type="button" class="primary" data-mo="grow" data-id="${o.id}">Invest to grow it</button>
      ${o.rebranded ? "" : `<button type="button" data-mo="rebrand" data-id="${o.id}">Put your brand on it (${maM(o.rebrand_cost)})</button>`}
      ${best ? `<button type="button" data-mo="sell" data-id="${o.id}">Sell it… (best bid ${maM(best.price)})</button>` : ""}
      <button type="button" data-mo="holdings" data-id="${o.id}">🌐 Industry moves in Global Holdings</button>
    </div>
  </article>`;
}

function maOwnedHtml(own) {
  if (!own || !Array.isArray(own.owned) || (!own.owned.length && !(own.sold || []).length)) return "";
  return `<section class="mo"><h3 class="mx-h">Companies you own</h3>
    <p class="mo-sub">🌐 They're all in <b>Global Holdings → Companies you own</b> too, with your industry's playbook and every next move.</p>
    ${own.owned.length ? `<div class="mx-grid">${own.owned.map(maOwnedCard).join("")}</div>` : `<p class="mo-sub">You've sold everything you bought.</p>`}
    ${(own.sold || []).length ? `<details class="mo-sold"><summary>Sold (${own.sold.length})</summary><table class="sx-table">
      <tr><th>Company</th><th>Buyer</th><th class="num">Paid</th><th class="num">Sold for</th><th class="num">Gain / loss</th><th class="num">Month</th></tr>
      ${own.sold.map((x) => `<tr><td>${esc(x.name)}</td><td>${esc(x.buyer)}</td><td class="num">${maM(x.paid)}</td><td class="num">${maM(x.sold_price)}</td>
        <td class="num ${x.gain >= 0 ? "pos" : "neg"}">${x.gain >= 0 ? "+" : ""}${maM(x.gain)}</td><td class="num">M${x.month}</td></tr>`).join("")}</table></details>` : ""}
    <p class="lx-note">${esc(own.how)}</p></section>`;
}

function maModal(html) {
  const back = document.createElement("div");
  back.className = "modal-backdrop";
  back.innerHTML = `<div class="modal mo-modal" role="dialog"><button type="button" class="modal-x" data-x aria-label="Close">✕</button>${html}</div>`;
  document.body.appendChild(back);
  const close = () => back.remove();
  back.addEventListener("click", (e) => { if (e.target === back || e.target.closest("[data-x]")) close(); });
  return back;
}

async function maRun(type, params) {
  try {
    toast((await postJSON("/actions/execute", { action: { type, params } })).message);
    await window.refreshAll();
    return true;
  } catch (e) { toast(e.message, true); return false; }
}

function maGrowModal(o, cash) {
  const max = Math.max(50000, Math.round(Math.min(cash * 0.9, Math.max(o.revenue, 500000)) / 10000) * 10000);
  const back = maModal(`<span class="sx-eyebrow">Grow ${esc(o.name)}</span><h3>Invest in ${esc(o.name)}</h3>
    <p class="bk-sub">New sales people, marketing and capacity for this business. Every 4% of a year's revenue you put in adds about +1 pt of growth a month for 12 months (up to +2.5 pts).</p>
    <label class="iv-slide">How much <b id="mo-amt"></b><input type="range" id="mo-range" min="50000" max="${max}" step="10000" value="${Math.min(max, Math.round(o.revenue * 0.04 / 10000) * 10000 || 50000)}"></label>
    <div class="iv-after" id="mo-after"></div>
    <div class="modal-actions"><button type="button" data-x>Cancel</button><button type="button" class="primary" id="mo-go">Invest</button></div>`);
  const r = back.querySelector("#mo-range");
  const paint = () => {
    const pts = Math.min(2.5, (100 * 0.04 * +r.value) / Math.max(50000, o.revenue));
    back.querySelector("#mo-amt").textContent = maM(+r.value);
    back.querySelector("#mo-after").innerHTML = `<div><em>Extra growth</em><b>+${pts.toFixed(2)} pts/mo</b></div><div><em>For</em><b>12 months</b></div><div><em>Cash left</em><b>${maM(cash - +r.value)}</b></div>`;
  };
  r.addEventListener("input", paint); paint();
  back.querySelector("#mo-go").addEventListener("click", async (e) => {
    e.target.disabled = true; e.target.textContent = "Investing…";
    if (await maRun("ma_owned_grow", { acq_id: o.id, amount: +r.value })) back.remove(); else { e.target.disabled = false; e.target.textContent = "Invest"; }
  });
}

function maSellModal(o) {
  const back = maModal(`<span class="sx-eyebrow">Sell ${esc(o.name)}</span><h3>Who wants to buy ${esc(o.name)}</h3>
    <p class="bk-sub">This quarter's bids. Selling brings the cash in now, takes its goodwill off your balance sheet, and its ${o.employees} people and revenue leave with it. You paid ${maM(o.paid)}.</p>
    <div class="mo-bids">${o.buyers.map((b, i) => `<div class="mo-bid ${i === 0 ? "best" : ""}">
      <div><b>${esc(b.buyer)}</b><span>${esc(b.type)} · ${esc(b.pitch)}</span></div>
      <div class="mo-bid-num"><strong>${maM(b.price)}</strong><em class="${b.price - o.paid >= 0 ? "pos" : "neg"}">${b.price - o.paid >= 0 ? "+" : ""}${maM(b.price - o.paid)} vs what you paid</em></div>
      <button type="button" class="${i === 0 ? "primary" : ""}" data-sell-to="${esc(b.buyer)}">Sell to ${esc(b.buyer)}</button></div>`).join("")}</div>
    <div class="modal-actions"><button type="button" data-x>Keep it</button></div>`);
  back.querySelectorAll("[data-sell-to]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm(`Sell ${o.name} to ${b.dataset.sellTo}?`)) return;
    b.disabled = true;
    if (await maRun("ma_owned_sell", { acq_id: o.id, buyer: b.dataset.sellTo })) back.remove(); else b.disabled = false;
  }));
}

window.renderMA = async function (main) {
  const own = await uiGet("/ma/owned").catch(() => null);
  if (!main.dataset.moWired) {
    // one delegated handler: the thread view redraws this tab's HTML often
    main.dataset.moWired = "1";
    main.addEventListener("click", async (e) => {
      const b = e.target.closest("[data-mo]");
      if (!b || !main.__maOwned || !Array.isArray(main.__maOwned.owned)) return;
      const o = main.__maOwned.owned.find((x) => x.id === +b.dataset.id);
      if (!o) return;
      if (b.dataset.mo === "holdings") { window.__ghOpen = `ma:${o.id}`; window.__ghSeg = "companies"; if (window.ges) window.ges.showTab("countries"); }
      else if (b.dataset.mo === "grow") maGrowModal(o, main.__maOwned.cash);
      else if (b.dataset.mo === "sell") maSellModal(o);
      else if (b.dataset.mo === "rebrand" && confirm(`Rebrand ${o.name} under your name for ${maM(o.rebrand_cost)}? +6% revenue on the line and +2 brand.`)) {
        b.disabled = true;
        if (!(await maRun("ma_owned_rebrand", { acq_id: o.id }))) b.disabled = false;
      }
    });
  }
  main.__maOwned = own;
  return thUI(main, {
    key: "ma",
    title: "Acquisitions",
    subtitle: "Your team brings you companies worth buying. Open a deal to read its story and decide the next step.",
    load: () => uiGet("/ma/threads"),
    threadsTitle: "Your deals",
    itemsTitle: "For sale this month",
    emptyThreads: "No deals yet. Pick a company for sale below.",
    emptyItems: "Nothing new. More companies come to market next month.",
    emptyText: "Your desk is clear.",
    summary: (d) => [
      { label: "Cash to spend", value: uiMoney(d.summary.cash, { compact: true }) },
      { label: "Banks would lend up to", value: uiMoney(d.summary.max_loan, { compact: true }) },
      { label: "Deals in progress", value: d.summary.offers },
      { label: "Companies you own", value: d.summary.owned },
    ],
    threadsOf: (d) => d.threads,
    itemsOf: (d) => {
      const passed = maPassed();
      return d.opportunities.filter((p) => passed[p.id] !== d.month);
    },
    onDismiss: (id, d) => {
      const passed = maPassed();
      passed[id] = d.month;
      try { localStorage.setItem(MA_PASSED_KEY, JSON.stringify(passed)); } catch (e) { /* private mode */ }
    },
    // After acting on a pitch, open the deal it created (or the purchase).
    afterAction: async (action) => {
      const name = action.params && action.params.target_name;
      if (!name) return null;
      const fresh = await uiGet("/ma/threads");
      const t = fresh.threads.find((x) => x.title === name);
      return t ? { type: "thread", id: t.id } : null;
    },
    footer: () => maOwnedHtml(own),
  });
};
